import * as React from "react";
import { useEffect, useState, Fragment, useRef } from "react";
import { translate } from "../../../translate/translate";
import { Box, Fab, Grid, Paper, TextField, Typography, FormControl, InputLabel, Select, MenuItem, Button, ButtonGroup, ClickAwayListener, Divider, Grow, MenuList, Popper } from "@mui/material";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import { BaseService } from "../../../services/base-service";
import { Download, Search } from "@mui/icons-material";
import { URLS } from "../../../services/app-urls";
import { PaginatedResult } from "../../../models/default/paginated-result";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import { ErrorComponent } from "../../../components/error/error.component";
import { FlagsSap, PoComponent } from "../../../../app/models/general/po-component";
import { parseLocaleNumber } from "../../../../app/utils/locale-number-utils";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { useExportProgress } from "../../../hooks/useExportProgress";
import { useAuth } from "../../../contexts/auth-context";
import { ExportStatus, ExportType } from "../../../types/export.types";
import { buildPoComponentsFileName, downloadExportFile } from "../../../utils/download-utils";
import Loading from "../../../components/loading/loading";
import { validateExportableData, validateExportApiResponse } from "../../../utils/export-validation.utils";

type ExportOption = "filtered" | "complete";

const exportOptions: ExportOption[] = ["filtered", "complete"];
const matlTypeOptions = ["ZROH", "ZHLB", "ZMOD"];
const LEFT_ALIGNED_BODY_COLUMNS = ["description", "supplier_name", "purchase_order_number", "invoice", "modified_at"];
const LEFT_ALIGNED_HEADER_COLUMNS = ["description", "supplier", "po", "invoice", "modified_at"];


const POComponent = () => {
  const service = new BaseService<PoComponent>(URLS.PO_COMPONENT);
  const serviceExportPO = new BaseService<Blob>(URLS.PO_COMPONENT);
  const { user } = useAuth();
  const { start: startExport } = useExportProgress(ExportType.EXPORT_PO_COMPONENTS);

  const [dataSource, setDataSource] = useState<PoComponent[]>([]);
  const [pagination, setPagination] = useState<PaginatedResult<any>>();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInvoice, setSearchInvoice] = useState("");
  const [searchPartNumber, setSearchPartNumber] = useState("");
  const [searchPo, setSearchPo] = useState("");
  const [searchMatlType, setSearchMatlType] = useState("all");
  const [searchSapFlag, setSearchSapFlag] = useState("all");

  const [showSplitExportButton, setShowSplitExportButton] = useState(false);
  const [selectedExportOption, setSelectedExportOption] = useState<ExportOption>("filtered");
  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const splitExportButtonRef = useRef<HTMLDivElement>(null);

  const handleChangeSapFlag = (e: any) => {
    const value = e.target.value;
    setSearchSapFlag(value);
    if (page === 0) {
      search();
    } else {
      setPage(0);
    }
  };
  const [sapFlagChoices, setSapFlagChoices] = useState<any[]>([]);

  useEffect(() => {
    service.getChoices && service.getChoices("sap_flags").then((choices: any) => {
      const statusItems: any[] = [];
      choices.forEach((choice: any) => {
        if (choice.value == 0) {
          statusItems.push({
            value: "waiting_invoice",
            display_name: translate.t("waiting_invoice"),
          });
          statusItems.push({
            value: "material_in_transit",
            display_name: translate.t("material_in_transit"),
          });
        } else {
          statusItems.push({
            value: choice.value,
            display_name: translate.t("expired_date"),
          });
        }
      });
      setSapFlagChoices(statusItems);
    });
  }, []);

  const handleExportPO = async () => {
    // Validate data before export
    const validation = validateExportableData(dataSource, ExportType.EXPORT_PO_COMPONENTS);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    try {
      serviceExportPO.clearParameters();

      const shouldAddFilter = selectedExportOption === "filtered";
      if (shouldAddFilter) {
        if (searchInvoice) {
          const formattedInvoice = searchInvoice.split(';').map(s => s.trim()).filter(Boolean).join(';');
          serviceExportPO.addParameter("invoice", formattedInvoice);
        }
        if (searchPartNumber) {
          const formattedPartNumber = searchPartNumber.split(';').map(s => s.trim()).filter(Boolean).join(';');
          serviceExportPO.addParameter("part_number", formattedPartNumber);
        }
        if (searchPo) {
          const formattedPo = searchPo.split(';').map(s => s.trim()).filter(Boolean).join(';');
          serviceExportPO.addParameter("po", formattedPo);
        }
        if (searchMatlType && searchMatlType !== "all") serviceExportPO.addParameter("matl_type", searchMatlType);
        if (searchSapFlag && searchSapFlag !== "all") {
          if (searchSapFlag === "waiting_invoice") {
            serviceExportPO.addParameter("sap_flags", "0");
            serviceExportPO.addParameter("status", "WAITING_INVOICE");
          } else if (searchSapFlag === "material_in_transit") {
            serviceExportPO.addParameter("sap_flags", "0");
            serviceExportPO.addParameter("status", "IN_TRANSIT");
          } else {
            serviceExportPO.addParameter("sap_flags", searchSapFlag);
          }
        }
      }

      const exportStartUrl = `${URLS.PO_COMPONENT}export/`;
      const startResponse = await serviceExportPO.loadUrlTyped<{
        code?: string;
        error?: string;
        id?: number;
        task_id?: string;
        file_url?: string;
        status?: string;
      }>(exportStartUrl);

      // Validate API response for NO_DATA_AVAILABLE error
      const apiValidation = validateExportApiResponse(startResponse.data);
      if (!apiValidation.isValid) {
        toast.error(apiValidation.message || translate.t("invalid_api_response"));
        return;
      }

      const { task_id, file_url, status } = startResponse.data;
      const fileName = buildPoComponentsFileName(isFiltering);

      if (!task_id && status === ExportStatus.FINISHED && file_url) {
        toast.success(translate.t("export_already_created_downloading"));

        try {
          await downloadExportFile(file_url, { fileName }, "po-components");
        } catch (downloadError) {
          toast.error(translate.t("error_downloading_file_try_again"));
        }
        return;
      }

      if (task_id && user?.id) {
        startExport(isFiltering);
        toast.info(translate.t("export_started_notification"));
      } else {
        toast.error(translate.t("export_error"));
      }
    } catch (error) {
      console.error(translate.t("export_error"), error);
    }
  };

  const handleMenuItemClick = (
    _: React.MouseEvent<HTMLLIElement, MouseEvent>,
    option: ExportOption,
  ) => {
    setSelectedExportOption(option);
    setShowSplitExportButton(false);
  };

  const toggleExportOptionsMenu = () => {
    setShowSplitExportButton(prevState => !prevState);
  };

  const closeExportOptionsMenu = (event: Event) => {
    if (splitExportButtonRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    setShowSplitExportButton(false);
  };

  const getExportButtonLabel = (option: ExportOption) => {
    return translate.t(
      option === "filtered" ? "export_filtered_analysis" : "export_complete_analysis",
    );
  };

  const search = () => {
    setIsLoading(true);
    service.clearParameters();
    if (searchInvoice) {
      const formattedInvoice = searchInvoice.split(';').map(s => s.trim()).filter(Boolean).join(';');
      service.addParameter("invoice", formattedInvoice);
    }
    if (searchPartNumber) {
      const formattedPartNumber = searchPartNumber.split(';').map(s => s.trim()).filter(Boolean).join(';');
      service.addParameter("part_number", formattedPartNumber);
    }
    if (searchPo) {
      const formattedPo = searchPo.split(';').map(s => s.trim()).filter(Boolean).join(';');
      service.addParameter("po", formattedPo);
    }
    if (searchMatlType && searchMatlType !== "all") service.addParameter("matl_type", searchMatlType);
    if (searchSapFlag && searchSapFlag !== "all") {
      if (searchSapFlag === "waiting_invoice") {
        service.addParameter("sap_flags", "0");
        service.addParameter("status", "WAITING_INVOICE");
      } else if (searchSapFlag === "material_in_transit") {
        service.addParameter("sap_flags", "0");
        service.addParameter("status", "IN_TRANSIT");
      } else {
        service.addParameter("sap_flags", searchSapFlag);
      }
    }
    service.addParameter("limit", rowsPerPage);
    service.addParameter("offset", page * rowsPerPage);

    // Atualiza isFiltering após executar o search
    const filtering = Boolean(
      searchInvoice ||
      searchPartNumber ||
      searchPo ||
      (searchMatlType && searchMatlType !== "all") ||
      (searchSapFlag && searchSapFlag !== "all")
    );
    setIsFiltering(filtering);

    service
      .getAllPaginate()
      .then((response) => {
        setPagination(response.data);
        setDataSource(response.data.results);
      })
      .catch((error: any) => {
        ErrorComponent(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    search();
  }, [page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleStatus = (row: any) => {
    service
      .update({ active: !row.active }, row.id)
      .then(() => {
        toast.success(translate.t("status_updated"));
        search();
      })
      .catch((error: any) => {
        ErrorComponent(error);
      });
  };

  const renderCellHandler = (
    column: string,
    _index: number,
    row: any,
  ) => {
    if (column === "quantity") {
      return parseLocaleNumber(row[column]);
    }
    if (column === "sap_flags") {
      const statusIndicator = <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: row["sap_flags"] === FlagsSap.OK ? "#4CAF4F" : "#F44336",
        }}
      />
      const statusLabel = row["sap_flags"] === FlagsSap.PAST_DATE_ERROR ? translate.t("expired_date") : row["invoice"] ? translate.t("material_in_transit") : translate.t("waiting_invoice");
      return (
        <Box display="flex" alignItems="center" justifyContent="center" gap={1} sx={{ lineHeight: 1 }}>
          {statusIndicator}
          <Typography variant="body2" sx={{ color: "#90a4ae", lineHeight: 1 }}>{statusLabel}</Typography>
        </Box>
      )
    }
    return row[column];
  };

  return (
    <section>
      <Loading open={isLoading} />
      <div className="floating-title" style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <Typography variant={"h5"}>{translate.t("purchase_orders")}</Typography>
      </div>

      <Grid item xs={12}>
        <Paper variant="outlined">
          <Box p={2}>
            <Formik
              enableReinitialize={true}
              initialValues={{}}
              onSubmit={(_, { setSubmitting }) => {
                if (page === 0) {
                  search();
                } else {
                  setPage(0);
                }
                setSubmitting(false);
              }}
            >
              {() => (
                <Form autoComplete="off">
                  <Grid alignItems="center" container direction={"column"} gap={2}>
                    <Grid alignItems="center" container spacing={2} direction={"row"}>
                      <Grid item xs={4}>
                        <TextField
                          label={translate.t("part_number")}
                          fullWidth
                          variant={"outlined"}
                          value={searchPartNumber}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchPartNumber(event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel id="filter-matl-type-label">
                            {translate.t("matl_type")}
                          </InputLabel>
                          <Select
                            labelId="filter-matl-type-label"
                            value={searchMatlType}
                            onChange={(event) => setSearchMatlType(event.target.value)}
                            label={translate.t("matl_type")}
                          >
                            <MenuItem value="all">{translate.t("all")}</MenuItem>
                            {matlTypeOptions.map((matlType) => (
                              <MenuItem key={matlType} value={matlType}>{matlType}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label={translate.t("po")}
                          fullWidth
                          variant={"outlined"}
                          value={searchPo}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchPo(event.target.value)}
                        />
                      </Grid>
                    </Grid>

                    <Grid alignItems="center" container spacing={2} direction={"row"}>
                      <Grid item xs={4}>
                        <TextField
                          label={translate.t("invoice")}
                          placeholder={translate.t("invoice_search_placeholder")}
                          fullWidth
                          variant={"outlined"}
                          value={searchInvoice}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchInvoice(event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel id="filter-sap-flag-label">
                            {translate.t("status")}
                          </InputLabel>
                          <Select
                            labelId="filter-sap-flag-label"
                            value={searchSapFlag}
                            onChange={handleChangeSapFlag}
                            label={translate.t("status")}
                          >
                            <MenuItem value="all">{translate.t("all")}</MenuItem>
                            {sapFlagChoices && sapFlagChoices.map((choice) => (
                              <MenuItem key={choice.value} value={choice.value}>{choice.display_name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item>
                        <Divider
                          orientation="vertical"
                          variant="fullWidth"
                          sx={{ height: "40px" }}
                        />
                      </Grid>
                      <Grid item sx={{ display: "flex", gap: 1 }}>
                        <Fab size="small" type="submit" color="primary">
                          <Search />
                        </Fab>
                        {(searchInvoice || searchPartNumber || searchPo || (searchMatlType && searchMatlType !== "all") || (searchSapFlag && searchSapFlag !== "all")) ? (
                          <Fragment>
                            <ButtonGroup
                              variant="outlined"
                              color="primary"
                              ref={splitExportButtonRef}
                              aria-label="split button for export options"
                              disabled={!dataSource || dataSource.length === 0}
                            >
                              <Button startIcon={<Download />} onClick={handleExportPO}>
                                {getExportButtonLabel(selectedExportOption)}
                              </Button>
                              <Button
                                size="small"
                                aria-controls={
                                  showSplitExportButton ? "split-button-menu" : undefined
                                }
                                aria-expanded={showSplitExportButton ? "true" : undefined}
                                aria-label="select export option"
                                aria-haspopup="menu"
                                onClick={toggleExportOptionsMenu}
                              >
                                <ArrowDropDownIcon />
                              </Button>
                            </ButtonGroup>
                            <Popper
                              sx={{
                                zIndex: 3,
                              }}
                              open={showSplitExportButton}
                              anchorEl={splitExportButtonRef.current}
                              role={undefined}
                              transition
                              disablePortal
                            >
                              {({ TransitionProps, placement }) => (
                                <Grow
                                  {...TransitionProps}
                                  style={{
                                    transformOrigin:
                                      placement === "bottom" ? "center top" : "center bottom",
                                  }}
                                >
                                  <Paper>
                                    <ClickAwayListener onClickAway={closeExportOptionsMenu}>
                                      <MenuList id="split-button-menu" autoFocusItem>
                                        {exportOptions.map(option => (
                                          <MenuItem
                                            key={option}
                                            selected={option === selectedExportOption}
                                            onClick={event => handleMenuItemClick(event, option)}
                                          >
                                            {getExportButtonLabel(option)}
                                          </MenuItem>
                                        ))}
                                      </MenuList>
                                    </ClickAwayListener>
                                  </Paper>
                                </Grow>
                              )}
                            </Popper>
                          </Fragment>
                        ) : (
                          <Button
                            startIcon={<Download />}
                            variant="outlined"
                            color="primary"
                            onClick={handleExportPO}
                            disabled={!dataSource || dataSource.length === 0}
                          >
                            {translate.t("export_analysis")}
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Box>
        </Paper>
      </Grid>

      <Paper variant="outlined" sx={{ marginTop: "16px" }}>
        <TablePaginationComponent
          state={dataSource}
          service={service}
          displayedColumnsShow={["status", "delivery_date", "part_number", "matl_type", "description", "supplier", "po", "invoice", "quantity", "modified_at"]}
          displayedColumns={["sap_flags", "estimated_time_delivery", "part_number", "matl_type", "description", "supplier_name", "purchase_order_number", "invoice", "quantity", "modified_at"]}
          count={pagination?.count}
          next={pagination?.next}
          previous={pagination?.previous}
          dateColumns={["modified_at","estimated_time_delivery"]}
          page={page}
          onPageChange={handleChangePage}
          handleSwitch={handleToggleStatus}
          renderCellHandler={{ "quantity": renderCellHandler, "sap_flags": renderCellHandler }}
          field={"sap_flags"}
          dateWithoutTime={["estimated_time_delivery"]}
          cellCustomStyleHandler={(column: string) => LEFT_ALIGNED_BODY_COLUMNS.includes(column) ? { textAlign: "left" } : { textAlign: "center" }}
          headerCellCustomStyleHandler={(column: string) => LEFT_ALIGNED_HEADER_COLUMNS.includes(column) ? { textAlign: "left" } : { textAlign: "center" }}
          rowsPerPage={rowsPerPage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          tableLayout="auto"
          tableMinWidth={1330}
          columnStyles={{
            sap_flags: { width: "9%", minWidth: 140, maxWidth: 180 },
            estimated_time_delivery: { width: "9%", minWidth: 130, maxWidth: 160 },
            part_number: { width: "11%", minWidth: 140, maxWidth: 200 },
            matl_type: { width: "7%", minWidth: 110, maxWidth: 130 },
            description: { width: "18%", minWidth: 200 },
            supplier_name: { width: "12%", minWidth: 150 },
            purchase_order_number: { width: "9%", minWidth: 130, maxWidth: 160 },
            invoice: { width: "9%", minWidth: 130, maxWidth: 180 },
            quantity: { width: "7%", minWidth: 110, maxWidth: 130 },
            modified_at: { width: "9%", minWidth: 140, maxWidth: 170 },
          }}
        />
      </Paper>
    </section>
  );
};

export default POComponent;
